import {
  useSite,
  useSiteSetupActions,
} from "@inrange/building-manager-api-client";
import {
  Site,
  SiteElectricalSystem,
} from "@inrange/building-manager-api-client/models-site";
import { Nav } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import Loading from "../Loading";
import SiteHealthSetup from "./SiteHealthSetup";
import SiteHealthStatus from "./SiteHealthStatus";
import SiteNav from "./siteSections/SiteNav";

const SiteHealthContent = ({
  site,
  siteId,
}: {
  site: Site;
  siteId: string;
}) => {
  const { tab } = useParams<{ tab: string }>();

  const isDemo = site.operationalStatus === "operationalDemo";

  const esysEntries = Object.entries(site.esys).sort(
    ([, a], [, b]) =>
      (a as SiteElectricalSystem).idx - (b as SiteElectricalSystem).idx
  ) as [string, SiteElectricalSystem][];
  const esysUuids = esysEntries.map(([, esys]) => esys.uuid);

  const {
    fetchReadingsTables,
    createReadingsTables,
    populateDemoActuals,
    ingestionByEsys,
    updateIngestion,
    deleteIngestion,
    discoverReduxiDevices,
    registerSoracomSim,
    importSoracomSim,
    simStatusByEsys,
  } = useSiteSetupActions({ siteId: site.id, esysUuids, enabled: true });

  const activeTab = isDemo || tab === "setup" ? "setup" : "status";

  document.title =
    activeTab === "setup"
      ? `Site: ${site.name} - Health Setup`
      : `Site: ${site.name} - Health`;

  return (
    <div style={{ padding: "8px 12px 0" }}>
      <Nav variant="tabs">
        {!isDemo && (
          <Nav.Item>
            <Nav.Link
              as={Link}
              to={`/site/${siteId}/health/status`}
              active={activeTab === "status"}
              replace
            >
              Status
            </Nav.Link>
          </Nav.Item>
        )}
        <Nav.Item>
          <Nav.Link
            as={Link}
            to={`/site/${siteId}/health/setup`}
            active={activeTab === "setup"}
            replace
          >
            Setup
          </Nav.Link>
        </Nav.Item>
      </Nav>
      <div className="tab-content">
        {activeTab === "status" && (
          <SiteHealthStatus
            site={site}
            isDemo={isDemo}
            esysEntries={esysEntries}
            ingestionByEsys={ingestionByEsys}
            simStatusByEsys={simStatusByEsys}
          />
        )}
        {activeTab === "setup" && (
          <SiteHealthSetup
            site={site}
            isDemo={isDemo}
            esysEntries={esysEntries}
            ingestionByEsys={ingestionByEsys}
            simStatusByEsys={simStatusByEsys}
            registerSoracomSim={registerSoracomSim}
            importSoracomSim={importSoracomSim}
            fetchReadingsTables={fetchReadingsTables}
            createReadingsTables={createReadingsTables}
            populateDemoActuals={populateDemoActuals}
            discoverReduxiDevices={discoverReduxiDevices}
            updateIngestion={updateIngestion}
            deleteIngestion={deleteIngestion}
          />
        )}
      </div>
    </div>
  );
};

const SiteHealthTabs = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const { fetchSite } = useSite({ siteId, app: "admin" });
  const site = fetchSite.data?.site;

  if (!fetchSite.isLoading && fetchSite.isError) {
    return <div>Error loading the site.</div>;
  }

  if (fetchSite.isLoading || !site) {
    return <Loading label="Loading site data..." />;
  }

  return (
    <>
      <SiteNav />
      <div style={{ padding: "12px", fontSize: "26px" }}>
        {fetchSite?.data?.site?.name}
      </div>
      <SiteHealthContent site={site} siteId={siteId!} />
    </>
  );
};

export default SiteHealthTabs;
